import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import type {
  ProcessoArbitralDraftDetail,
  ProcessoArbitralDraftListResponse,
  ProcessoArbitralDraftSnapshot,
  ProcessoArbitralDraftStep,
} from '@/features/processo-arbitral/types/rascunhoStepTypes';

interface SuccessEnvelope<T> {
  success: boolean;
  data?: T;
  error?:
    | {
        message?: string;
      }
    | string;
  message?: string;
}

export class ProcessoArbitralDraftRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ProcessoArbitralDraftRequestError';
    this.status = status;
  }
}

const extractApiErrorMessage = (body: unknown): string | null => {
  if (typeof body !== 'object' || body === null) {
    return null;
  }

  const record = body as Record<string, unknown>;
  const directMessage = record.message;
  const directError = record.error;

  if (typeof directMessage === 'string' && directMessage.trim()) {
    return directMessage;
  }

  if (typeof directError === 'string' && directError.trim()) {
    return directError;
  }

  if (typeof directError === 'object' && directError !== null) {
    const nestedMessage = (directError as Record<string, unknown>).message;

    if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
      return nestedMessage;
    }
  }

  return null;
};

const handleApiError = async (response: Response, defaultMessage: string): Promise<never> => {
  const body = (await response.json().catch(() => null)) as unknown;

  throw new ProcessoArbitralDraftRequestError(
    extractApiErrorMessage(body) ?? defaultMessage,
    response.status,
  );
};

const unwrapEnvelope = <T>(body: unknown): T => {
  if (typeof body === 'object' && body !== null && 'success' in body) {
    const envelope = body as SuccessEnvelope<T>;

    if (envelope.success && envelope.data !== undefined) {
      return envelope.data;
    }

    if (typeof envelope.error === 'string' && envelope.error.trim()) {
      throw new Error(envelope.error);
    }

    if (typeof envelope.error === 'object' && envelope.error?.message) {
      throw new Error(envelope.error.message);
    }

    throw new Error(envelope.message || 'Erro ao processar rascunho arbitral');
  }

  return body as T;
};

export const processoArbitralRascunhoStepService = {
  createDraft: async (input?: {
    ultimoPasso?: ProcessoArbitralDraftStep;
    snapshot?: ProcessoArbitralDraftSnapshot;
  }): Promise<ProcessoArbitralDraftDetail> => {
    const response = await apiPost('/arbitral/processos/rascunhos', {
      ultimoPasso: input?.ultimoPasso,
      payload: input?.snapshot,
    });

    if (!response.ok) {
      await handleApiError(response, 'Erro ao criar rascunho arbitral');
    }

    return unwrapEnvelope<ProcessoArbitralDraftDetail>((await response.json()) as unknown);
  },

  updateDraft: async (
    draftId: string,
    input: {
      ultimoPasso?: ProcessoArbitralDraftStep;
      snapshot: ProcessoArbitralDraftSnapshot;
    },
  ): Promise<ProcessoArbitralDraftDetail> => {
    const response = await apiPut(`/arbitral/processos/rascunhos/${draftId}`, {
      ultimoPasso: input.ultimoPasso,
      payload: input.snapshot,
    });

    if (!response.ok) {
      await handleApiError(response, 'Erro ao atualizar rascunho arbitral');
    }

    return unwrapEnvelope<ProcessoArbitralDraftDetail>((await response.json()) as unknown);
  },

  getById: async (draftId: string): Promise<ProcessoArbitralDraftDetail | null> => {
    const response = await apiGet(`/arbitral/processos/rascunhos/${draftId}`);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao carregar rascunho arbitral');
    }

    return unwrapEnvelope<ProcessoArbitralDraftDetail | null>((await response.json()) as unknown);
  },

  getLatest: async (): Promise<ProcessoArbitralDraftDetail | null> => {
    const response = await apiGet('/arbitral/processos/rascunhos/ultimo');

    if (!response.ok) {
      await handleApiError(response, 'Erro ao carregar último rascunho arbitral');
    }

    return unwrapEnvelope<ProcessoArbitralDraftDetail | null>((await response.json()) as unknown);
  },

  list: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ProcessoArbitralDraftListResponse> => {
    const response = await apiGet('/arbitral/processos/rascunhos', {
      page: params?.page ?? 1,
      limit: params?.limit ?? 50,
      status: 'RASCUNHO',
      ...(params?.search ? { search: params.search } : {}),
    });

    if (!response.ok) {
      await handleApiError(response, 'Erro ao listar rascunhos arbitrais');
    }

    return unwrapEnvelope<ProcessoArbitralDraftListResponse>((await response.json()) as unknown);
  },

  discard: async (draftId: string): Promise<void> => {
    const response = await apiDelete(`/arbitral/processos/rascunhos/${draftId}`);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao descartar rascunho arbitral');
    }
  },
};
